import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AudioExperienceTile } from '../audio-experience-tile';
import { VideoExperienceTile } from '../video-experience-tile';
import { Content, Experience } from '../../hooks/api/useExperiences';
import { addVimeoMetadataToExperiences, fetchVimeoMetadataBatch } from '../../hooks/api/useBrowseExperiences';
import { PeersonaReferralInfo } from '../../hooks/api/usePeersonaChat';
import { ROUTE_PATH } from '../../routes/route-paths';
import { PEERSONA_REFERRAL_ACCENT } from './peersona-styles';

interface Props {
  referral: PeersonaReferralInfo;
}

const asExperience = (raw: Record<string, unknown>): (Experience | Content) | null => {
  if (typeof raw.id !== 'number') return null;
  return raw as unknown as Experience | Content;
};

/** Build a minimal Content-shaped object when only flat referral fields exist. */
function referralToExperience(referral: PeersonaReferralInfo): Content | null {
  if (!referral.audio_id) return null;
  return {
    id: referral.audio_id,
    ai_title: referral.audio_title,
    title: referral.audio_title,
    excerpt: referral.audio_excerpt ?? '',
    ai_summary: referral.audio_excerpt ?? '',
    listener_role_id: referral.listener_role_id,
    name: referral.listener_name,
    profile_photo_square_file_url: referral.profile_photo_url ?? '',
    file_path: referral.audio_url ?? '',
    audio_url: referral.audio_url ?? '',
    video_id: referral.media_type === 'video' ? '1' : '',
    media_type: referral.media_type === 'video' ? 'VIDEO' : 'AUDIO',
    duration: 0,
    admin_summary: '',
    area_ids: [],
    background_tags: [],
    client_id: 0,
    client_image_url: '',
    is_available: false,
    is_favorite: false,
    is_listener: false,
    peer_type: '',
    rating: 0,
    resource_client_id: 0,
    resource_id: 0,
    resource_image_url: '',
    score: 0,
    similarity_score: 0,
    tag_group_names: [],
    tag_names: [],
    transcription: '',
  };
}

/**
 * Referral affordance under Blake's bubble: one platform story tile plus
 * peer CTAs. No section header, no "see more" footer — the conversation
 * already named the peer; this is the actionable follow-through.
 */
export const PeersonaReferralContent: React.FC<Props> = ({ referral }) => {
  const history = useHistory();
  const peerName = referral.listener_name?.trim() || 'this peer';
  const rawItem = referral.items?.[0];
  const baseExperience = useMemo(
    () => (rawItem ? asExperience(rawItem) : referralToExperience(referral)),
    [rawItem, referral],
  );
  const [experience, setExperience] = useState<(Experience | Content) | null>(baseExperience);

  useEffect(() => {
    setExperience(baseExperience);
  }, [baseExperience]);

  useEffect(() => {
    if (!baseExperience) return;
    let cancelled = false;
    const run = async () => {
      const exp = baseExperience as Content;
      if (!exp.video_id || exp.vimeo_thumbnail_url) return;
      const metadataMap = await fetchVimeoMetadataBatch([exp.video_id]);
      if (cancelled) return;
      setExperience(addVimeoMetadataToExperiences(exp, metadataMap));
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [baseExperience]);

  if (!experience) return null;

  const isVideo =
    rawItem?.media_type === 'VIDEO' || (!rawItem?.media_type && rawItem?.video_id) || referral.media_type === 'video';

  const visitProfile = () => {
    history.push(`${ROUTE_PATH.PEER_DETAILS}?listenerId=${referral.listener_role_id}`);
  };

  const scheduleCall = () => {
    history.push(`${ROUTE_PATH.PEER_DETAILS}?listenerId=${referral.listener_role_id}&schedule=true`);
  };

  const canSchedule = Boolean(rawItem?.is_listener) || Boolean((experience as Content | null)?.is_listener);

  return (
    <div className="mt-2 flex flex-col gap-3">
      <div className="w-[194px] shrink-0">
        {isVideo ? (
          <VideoExperienceTile experience={experience} showTopicTag={false} />
        ) : (
          <AudioExperienceTile experience={experience} showTopicTag={false} />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={visitProfile}
          className="font-albert-sans inline-flex items-center justify-center rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors hover:bg-gray-50"
          style={{
            borderColor: PEERSONA_REFERRAL_ACCENT,
            color: PEERSONA_REFERRAL_ACCENT,
          }}
        >
          Visit {peerName}&apos;s profile
        </button>
        {canSchedule && (
          <button
            type="button"
            onClick={scheduleCall}
            className="font-albert-sans inline-flex items-center justify-center rounded-full px-4 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: PEERSONA_REFERRAL_ACCENT }}
          >
            Schedule a call
          </button>
        )}
      </div>
    </div>
  );
};
